<script>
  // Wareneingang — Positionsliste (A7/L3).
  //
  // Eine Lieferung bringt mehrere Rohstoffe; die Maske erfasst deshalb eine
  // Liste, die als GANZES gebucht wird. Datum und Beleg stehen im Kopf, sie
  // gelten für alle Positionen. Die Einzelbuchung ist der Sonderfall mit einer
  // Position und keine zweite Maske.
  //
  // Ein Markup, zwei Erscheinungen: unter 600 px Vollbild, darüber ein Dialog
  // über der Bestandsliste. Der Bruch liegt auf dem CONTAINER, nicht auf dem
  // Viewport — die Insel sitzt in einem Panel, dessen Breite maßgeblich ist.
  import { onMount, tick } from 'svelte';
  import ZutatSuche from '../teile/ZutatSuche.svelte';

  /** @type {{ onschliessen: () => void, vorwahl?: string|null }} */
  let { onschliessen, vorwahl = null } = $props();
  let formEl;

  const t  = (k, p) => window.t(k, p);
  const tn = (k, n, p) => window.tn(k, n, p);

  const heute = new Date();
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  // ── Vorbelegung aus dem Zutat-Detail (Plan 18.6) ─────────────────────────
  // Wer dort „Eingang buchen" drückt, hat die Zutat bereits gewählt. Die Maske
  // öffnet deshalb mit ihrer Position statt mit der Suche — und zwar SCHON BEIM
  // AUFBAU, nicht nachträglich in onMount: Sonst blitzt das Suchfeld auf, zieht
  // den Fokus an sich (ZutatSuche fokussiert beim Mounten) und verschwindet
  // wieder. Der Treffer kommt aus derselben Quelle wie ein gesuchter, damit es
  // für den Rest der Maske nur EINEN Fall gibt.
  // Genau EINMAL gelesen, und das ist der Punkt: Die Maske wird bei jedem
  // Öffnen frisch montiert (`{#if maskeOffen}` in Lager.svelte), eine
  // nachträglich wechselnde Vorwahl gibt es nicht.
  // svelte-ignore state_referenced_locally
  const vorbelegt = vorwahl ? (window.lagerTrefferFuer?.(vorwahl) || null) : null;

  let datum      = $state(iso(heute));
  let beleg      = $state('');
  let positionen = $state(vorbelegt
    ? [{ ...vorbelegt, menge: '', einheit: 'kg', key: window.neueId() }]
    : []);
  let suchtOffen = $state(!vorbelegt);
  let laeuft     = $state(false);
  // Der Kopf gilt einmal je Lieferung, die Suche braucht den Platz jedes Mal.
  // Solange gesucht wird, tritt er deshalb zurück — auf dem HANDY, wo der
  // Platz das knappe Gut ist; die Container-Abfrage im CSS entscheidet das,
  // nicht diese Zeile. `kopfAuf` ist der Weg zurück für den seltenen Fall,
  // dass jemand mitten in der Erfassung das Datum richtigstellen will.
  let kopfAuf    = $state(false);
  let kompakt    = $derived(suchtOffen && positionen.length > 0 && !kopfAuf);

  // Gebucht wird in Gramm; kg ist reine Eingabehilfe. Stück gibt es bewusst
  // nicht: Der automatische Abgang rechnet aus Rezepten in Gramm und käme an
  // eine Stück-Zutat nicht heran — sie würde ewig zugebucht und nie abgebucht.
  const EINHEITEN = ['kg', 'g'];

  // Cmd auf Apple-Geräten, Strg sonst. Der gelernte Standard fürs Absenden
  // eines Formulars mit Tastatur (Mail, Chat, Editoren) — hier der Weg, der
  // die vier Tab-Sprünge zum Sammelknopf erspart.
  const ISTMAC = typeof navigator !== 'undefined' &&
    /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || '');
  const TASTE = ISTMAC ? '⌘' : 'Strg';

  let datumText = $derived(datum
    ? new Date(`${datum}T12:00:00`).toLocaleDateString(window.currentLocale(),
        { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '—');

  let gesamtGramm = $derived(positionen.reduce((s, p) => s + _gramm(p), 0));
  let vollstaendig = $derived(positionen.filter((p) => _gramm(p) > 0));

  function _gramm(p) {
    const n = parseFloat(String(p.menge).replace(',', '.'));
    if (!Number.isFinite(n) || n <= 0) return 0;
    return Math.round(p.einheit === 'kg' ? n * 1000 : n);
  }

  /**
   * Enter heißt „weiter", nicht „abschicken".
   *
   * Ein Enter im Mengenfeld schickte bisher die ganze Lieferung ab — nach der
   * ERSTEN Position, also fast nie das, was gemeint war. Jetzt springt der
   * Fokus zurück in die Suche, und die nächste Zutat lässt sich sofort tippen.
   * Abgeschickt wird über den Sammelknopf, der von dort mit Tab erreichbar ist.
   */
  async function weiter(ev) {
    if (ev.key !== 'Enter') return;
    // Mit Zusatztaste ist es „abschicken", nicht „weiter" — durchlassen.
    if (ev.metaKey || ev.ctrlKey) return;
    ev.preventDefault();
    suchtOffen = true; kopfAuf = false;
    await tick();
  }

  async function hinzu(treffer) {
    positionen = [...positionen, { ...treffer, menge: '', einheit: 'kg', key: window.neueId() }];
    suchtOffen = false;
    // Der Fokus wandert in das Mengenfeld der neuen Zeile: Wer eine Zutat
    // gewählt hat, will als Nächstes die Menge tippen — nicht erst dorthin
    // greifen. Nach `tick`, weil die Zeile erst dann im DOM steht.
    await tick();
    const felder = formEl?.querySelectorAll('.lg-pos__menge');
    felder?.[felder.length - 1]?.focus();
  }

  function weg(key) {
    positionen = positionen.filter((p) => p.key !== key);
    if (!positionen.length) suchtOffen = true;
  }

  function einheitWechseln(p) {
    p.einheit = EINHEITEN[(EINHEITEN.indexOf(p.einheit) + 1) % EINHEITEN.length];
    positionen = positionen;
  }

  const mengeText = (g) => { const m = window.lagerMenge(g); return `${m.wert} ${m.einheit}`; };

  // Dasselbe Versprechen wie nach einer Wahl in der Suche: Wer die Zutat schon
  // hat, will als Nächstes die Menge tippen.
  onMount(() => {
    if (!vorbelegt) return;
    /** @type {HTMLInputElement|null|undefined} */
    const feld = formEl?.querySelector('.lg-pos__menge');
    feld?.focus();
  });

  async function buchen() {
    if (!vollstaendig.length || laeuft) return;
    laeuft = true;
    try {
      // Erst alle neuen Zutaten anlegen. Scheitert eine, wird NICHTS gebucht:
      // Eine halb erfasste Lieferung ist ein Zustand, den später niemand mehr
      // erklären kann.
      const fertig = [];
      // Zwei Positionen desselben neuen Namens sind der Normalfall — zwei Säcke
      // aus einer Lieferung, getrennt erfasst, weil sie getrennt im Regal
      // stehen. Ohne dieses Gedächtnis liefe die zweite in die Unique-Regel
      // („ein Name je Betrieb") und die ganze Lieferung wäre verworfen. Der
      // Schlüssel ist derselbe wie der des Index: Name, klein geschrieben.
      const angelegt = new Map();
      for (const p of vollstaendig) {
        let zutatId = p.zutatId || angelegt.get(p.name.trim().toLowerCase()) || null;
        if (!zutatId) {
          const neu = await window.zutatAnlegen({
            name: p.name, warengruppe: p.warengruppe, rechenrolle: p.rechenrolle,
          });
          if (!neu?.id) {
            window.notify({ level: 'err', channel: 'sticky', text: t('lager_fehler_anlegen', { name: p.name }) });
            return;
          }
          zutatId = neu.id;
          angelegt.set(p.name.trim().toLowerCase(), zutatId);
        }
        fertig.push({
          zutatId, menge: _gramm(p), art: 'eingang',
          ts: new Date(`${datum}T12:00:00`).toISOString(),
          notiz: beleg.trim() || null,
        });
      }

      const zeilen = await window.lagerBuchenViele(fertig);
      if (!zeilen) {
        window.notify({ level: 'err', channel: 'sticky', text: t('lager_fehler_buchen') });
        return;
      }

      // Die Meldung hält die Kennungen der geschriebenen Zeilen in ihrem
      // Abschluss — Rückgängig ist eine Gegenbuchung je Zeile, keine Löschung.
      const summe = zeilen.reduce((s, b) => s + b.menge, 0);
      window.notify({
        // transient: Die Meldung läuft nach der Frist der Stufe `ok` ab (5 s,
        // NOTICE_MS in notify.js) — dieselbe wie überall sonst in der App. Der
        // Rückgängig-Weg ist damit kurzlebig; dauerhaft rückgängig macht man
        // eine Buchung über den Verlauf der Zutat (Gegenbuchung, ab L7).
        level: 'ok',
        text: tn('lager_gebucht', zeilen.length, { count: zeilen.length, menge: mengeText(summe) }),
        cta: {
          label: t('lager_gebucht_rueckgaengig'),
          onClick: async () => {
            // In EINER Transaktion: Was als eine Lieferung gebucht wurde, wird
            // als eine zurückgenommen — halb storniert wäre ein Zustand, den
            // später niemand mehr erklären kann.
            const ok = await window.lagerStornierenViele(zeilen.map((b) => b.id));
            window.notify({ level: ok ? 'info' : 'err',
                            text: t(ok ? 'lager_storniert' : 'lager_fehler_buchen') });
          },
        },
      });
      onschliessen();
    } finally {
      laeuft = false;
    }
  }
</script>

<!-- Cmd/Strg+Enter schickt von überall in der Maske ab. Am FENSTER und nicht
     am <form>: Der Griff wirkt dann auch, wenn der Fokus gerade nirgends sitzt
     (etwa nach einem Klick auf den Schleier), und ein Formular ist kein
     bedienbares Element, an das Tastaturereignisse gehören. Die Komponente ist
     nur montiert, solange die Maske offen ist — der Griff also auch. -->
<svelte:window onkeydown={(e) => {
  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); buchen(); }
}} />

<div class="lg-host">
  <div class="lg-scrim" onclick={onschliessen} role="presentation"></div>
  <form class="lg-eb" class:lg-eb--sucht={kompakt} bind:this={formEl}
        onsubmit={(e) => { e.preventDefault(); buchen(); }}>

    <div class="lg-eb__head">
      <div class="lg-eb__title">{t('lager_eingang_titel')}</div>
      <button type="button" class="lg-eb__x" onclick={onschliessen} aria-label={t('lager_eingang_abbrechen')}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9"
             stroke-linecap="round" width="19" height="19" aria-hidden="true">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    </div>

    <!-- Kopf: gilt für die ganze Lieferung. Je Position wiederholt wäre es
         Tipparbeit für dieselbe Information. -->
    <div class="lg-eb__kopf">
      <!-- Die eingeklappte Fassung. Sie steht IMMER im Markup und wird von der
           Container-Abfrage ein- und ausgeblendet — der Bruch liegt am
           Container, nicht am Zustand der Komponente. -->
      <button type="button" class="lg-eb__kurzkopf" onclick={() => (kopfAuf = true)}>
        <span class="lg-eb__kurzkopf-wert">{datumText}{#if beleg.trim()}{' · ' + beleg.trim()}{/if}</span>
        <span class="lg-eb__kurzkopf-tu">{t('lager_eingang_kopf_aendern')}</span>
      </button>
      <div class="lg-eb__felder">
        <label>
          <span class="lg-lab">{t('lager_eingang_datum')}</span>
          <input type="date" bind:value={datum} class="lg-box">
        </label>
        <label>
          <span class="lg-lab">{t('lager_eingang_beleg')}</span>
          <input type="text" bind:value={beleg} class="lg-box" onkeydown={weiter}
                 placeholder={t('lager_eingang_beleg_ph')}>
        </label>
      </div>
      <p class="lg-eb__note">{t('lager_eingang_kopf_note')}</p>
    </div>

    <div class="lg-eb__liste">
      {#if positionen.length}
        <div class="lg-liste__kopf">{t('lager_eingang_positionen')}<b>{positionen.length}</b></div>
        {#each positionen as p (p.key)}
          <div class="lg-pos">
            <div class="lg-pos__name">
              {p.name}
              {#if !p.zutatId}<span class="lg-neu">{t('lager_pos_neu')}</span>{/if}
              <span class="lg-pos__meta">
                {p.warengruppe || '—'}{#if p.bestand !== null && p.bestand !== undefined} · {t('lager_pos_bestand', { menge: mengeText(p.bestand) })}{/if}
              </span>
            </div>
            <input class="lg-pos__menge" type="text" inputmode="decimal" bind:value={p.menge}
                   onkeydown={weiter} placeholder="0" aria-label={t('lager_menge_ph')}>
            <button type="button" class="lg-pos__einheit" onclick={() => einheitWechseln(p)}
                    aria-label={t('lager_einheit_wechseln')}>{p.einheit}</button>
            <button type="button" class="lg-pos__weg" onclick={() => weg(p.key)}
                    aria-label={t('lager_eingang_entfernen')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9"
                   stroke-linecap="round" width="17" height="17" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        {/each}
      {/if}

      <!-- Beim Öffnen IST die Suche die Maske. Ab der ersten Position schrumpft
           sie zur Zeile am Listenende, und die Liste wird das Hauptobjekt. -->
      <div class="lg-add" class:lg-add--hero={!positionen.length}
           class:lg-add--offen={suchtOffen && positionen.length > 0}>
        {#if suchtOffen || !positionen.length}
          {#if !positionen.length}<span class="lg-lab">{t('lager_eingang_suchen')}</span>{/if}
          <ZutatSuche onwahl={hinzu} />
        {:else}
          <button type="button" class="lg-add__btn" onclick={() => { suchtOffen = true; kopfAuf = false; }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1"
                 stroke-linecap="round" width="17" height="17" aria-hidden="true">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            {t('lager_eingang_weitere')}
          </button>
        {/if}
      </div>
    </div>

    <div class="lg-eb__foot">
      <p class="lg-eb__journal">
        {t('lager_eingang_journal_note')}
        {#if vollstaendig.length}
          <span class="lg-kurz">{t('lager_eingang_kurzbefehl', { taste: TASTE })}</span>
        {/if}
      </p>
      <button type="submit" class="lg-cta" disabled={!vollstaendig.length || laeuft}>
        {#if vollstaendig.length}
          {tn('lager_eingang_cta', vollstaendig.length, { count: vollstaendig.length, menge: mengeText(gesamtGramm) })}
        {:else}
          {t('lager_eingang_leer_cta')}
        {/if}
      </button>
    </div>

  </form>
</div>

<style>
  /* Der Bruch liegt auf dem Container, nicht auf dem Viewport: Die Insel sitzt
     in einem Panel, und dessen Breite entscheidet — nicht die des Fensters. */
  .lg-host { position: fixed; inset: 0; z-index: 60; display: flex; container-type: inline-size; }
  .lg-scrim { display: none; }
  .lg-eb { flex: 1 1 auto; min-height: 0; display: flex; flex-direction: column; background: #f0ede9; }

  .lg-eb__head {
    flex-shrink: 0; display: grid; grid-template-columns: 1fr 44px; align-items: center;
    gap: 8px; padding: 10px 10px 10px 16px; background: rgba(245, 243, 240, 0.94);
    border-bottom: 1px solid var(--lg-line);
  }
  .lg-eb__title { font-size: 16px; font-weight: 700; color: var(--lg-ink); letter-spacing: -0.015em; }
  .lg-eb__x {
    width: 44px; height: 44px; display: flex; align-items: center; justify-content: center;
    border: 0; border-radius: 10px; background: none; color: var(--lg-ink-soft); cursor: pointer;
  }
  .lg-eb__x:hover { background: rgba(41, 37, 36, 0.06); color: var(--lg-ink); }
  .lg-eb__x:focus-visible { outline: 2px solid var(--lg-ink); outline-offset: -2px; }

  .lg-eb__kopf { flex-shrink: 0; padding: 14px 16px; background: #fafaf9; border-bottom: 1px solid var(--lg-line); }
  .lg-eb__kurzkopf {
    display: none; align-items: center; gap: 10px; width: 100%; box-sizing: border-box;
    min-height: 44px; padding: 0 16px; border: 0; background: none; font: inherit;
    font-size: 12.5px; color: var(--lg-ink-soft); text-align: left; cursor: pointer;
  }
  .lg-eb__kurzkopf-wert { font-variant-numeric: tabular-nums; }
  .lg-eb__kurzkopf-tu {
    margin-left: auto; font-size: 10px; font-weight: 700; letter-spacing: 0.07em;
    text-transform: uppercase; color: var(--lg-ink-mute);
  }
  .lg-eb__kurzkopf:focus-visible { outline: 2px solid var(--lg-ink); outline-offset: -2px; }
  .lg-eb__felder { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
  /* ⚠ Rasterzellen dürfen schrumpfen. Ohne das steht `min-width: auto`, und
     eine Zelle wird nie schmaler als ihr Inhalt — das native Datumsfeld von
     iOS Safari bringt eine eigene Mindestbreite mit und schob sich damit über
     das Feld daneben. */
  .lg-eb__felder > label { min-width: 0; }
  .lg-eb__note { font-size: 11px; color: var(--lg-ink-mute); margin: 10px 0 0; }
  .lg-lab {
    display: block; font-size: 11px; font-weight: 700; letter-spacing: 0.07em;
    text-transform: uppercase; color: var(--lg-ink-mute); margin-bottom: 8px;
  }
  .lg-box {
    display: block; width: 100%; box-sizing: border-box; min-height: 48px; padding: 0 10px;
    background: var(--lg-card); border: 1px solid #d6d3d1; border-radius: 10px;
    font: inherit; font-size: 13.5px; color: var(--lg-ink);
  }
  .lg-box:focus { outline: none; border-color: var(--lg-ink); box-shadow: 0 0 0 3px rgba(41, 37, 36, 0.07); }
  /* Das Datumsfeld ist auf iOS ein natives Bedienelement, das seine Maße selbst
     bestimmt: flacher als die Nachbarn und breiter als seine Zelle. Erst ohne
     die eingebaute Darstellung gelten Höhe, Rahmen und Breite von oben auch
     dort. Der Kalender-Aufruf bleibt — angetippt wird das Feld, nicht ein
     Symbol darin. `max-width` ist der Riegel für den Fall, dass eine künftige
     Fassung sich doch wieder eigenmächtig breit macht. */
  /* ⚠ Und die Höhe allein genügt nicht: Der Wert ist ein Kind im Schatten des
     Feldes, und WebKit setzt ihn an den OBEREN Rand der Box statt in ihre
     Mitte — auf iOS klebte das Datum damit an der Oberkante, während „Beleg"
     daneben mittig stand. Nur ein Flex-Container zentriert ihn; `align-content`
     und Regeln auf `::-webkit-date-and-time-value` lassen WebKit unberührt
     (beides gemessen, in WebKit und Chromium).
     Das Flexen kostet in Chromium die rechte Lage des Kalendersymbols — dort
     ist es kein eigenes Flex-Kind mehr und rutscht an den Text heran. Deshalb
     hängt es daneben aus dem Fluss: Chromium stellt es wieder an den Rand,
     WebKit kennt unter `appearance: none` gar keines und ignoriert die Regel. */
  input.lg-box[type="date"] {
    -webkit-appearance: none; appearance: none;
    min-width: 0; max-width: 100%;
    position: relative;
    display: flex; align-items: center;
  }
  input.lg-box[type="date"]::-webkit-calendar-picker-indicator {
    position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
  }

  /* Jede Ebene traegt ihre eigene Flaeche. Sich auf die des Elternteils zu
     verlassen ging gut, solange die Tokens galten — und schlug fehl, sobald
     sie es nicht taten: Eine ungueltige Hintergrundfarbe ist transparent. */
  .lg-eb__liste { flex: 1 1 auto; min-height: 0; overflow-y: auto; background: #f0ede9; }
  .lg-liste__kopf {
    position: sticky; top: 0; z-index: 3; display: flex; align-items: baseline; gap: 8px;
    padding: 12px 16px 8px; background: #f0ede9; border-bottom: 1px solid var(--lg-line);
    font-size: 11px; font-weight: 700; letter-spacing: 0.09em; text-transform: uppercase;
    color: var(--lg-ink-mute);
  }
  .lg-liste__kopf b {
    margin-left: auto; font-size: 11.5px; font-weight: 600; letter-spacing: 0;
    text-transform: none; color: var(--lg-ink-soft); font-variant-numeric: tabular-nums;
  }
  .lg-pos {
    display: grid; grid-template-columns: minmax(0, 1fr) 78px 44px 44px; align-items: center;
    gap: 6px; min-height: 60px; padding: 8px 10px 8px 16px;
    background: var(--lg-card); border-bottom: 1px solid #f5f5f4;
  }
  .lg-pos__name {
    display: flex; flex-wrap: wrap; align-items: baseline; gap: 6px 8px; min-width: 0;
    font-size: 14.5px; font-weight: 600; color: var(--lg-ink); letter-spacing: -0.005em;
  }
  .lg-pos__meta {
    flex-basis: 100%; white-space: nowrap; font-size: 11px; font-weight: 400;
    color: var(--lg-ink-mute); font-variant-numeric: tabular-nums;
  }
  .lg-pos__menge {
    height: 44px; box-sizing: border-box; padding: 0 10px; text-align: right;
    background: var(--lg-card); border: 1px solid #d6d3d1; border-radius: 10px;
    font: inherit; font-size: 15px; font-weight: 700; color: var(--lg-ink);
    font-variant-numeric: tabular-nums; min-width: 0;
  }
  .lg-pos__menge:focus { outline: none; border-color: var(--lg-ink); box-shadow: 0 0 0 3px rgba(41, 37, 36, 0.07); }
  /* Gepunktet unterstrichen wie im Korrektur-Sheet — derselbe Umschalter
     muss in beiden Masken dasselbe Zeichen tragen (Pruefblatt 2026-09-15). */
  .lg-pos__einheit {
    height: 44px; border: 0; border-radius: 10px; background: none; font: inherit;
    font-size: 12.5px; font-weight: 700; color: var(--lg-ink-soft); cursor: pointer;
    text-decoration: underline dotted;
    text-decoration-color: var(--lg-ink-mute);
    text-underline-offset: 4px;
  }
  .lg-pos__einheit:hover { background: rgba(41, 37, 36, 0.06); color: var(--lg-ink); }
  .lg-pos__einheit:focus-visible { outline: 2px solid var(--lg-ink); outline-offset: -2px; }
  .lg-pos__weg {
    width: 44px; height: 44px; display: flex; align-items: center; justify-content: center;
    border: 0; border-radius: 10px; background: none; color: var(--lg-ink-mute); cursor: pointer;
  }
  .lg-pos__weg:hover { background: #fef2f2; color: #dc2626; }
  .lg-pos__weg:focus-visible { outline: 2px solid #dc2626; outline-offset: -2px; color: #dc2626; }
  .lg-neu {
    white-space: nowrap; display: inline-flex; align-items: center; padding: 2px 8px;
    border-radius: 999px; background: #e8eee0; color: #4d5c3b; font-size: 10px;
    font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase;
  }

  /* Klebt am unteren Rand des Scrollbereichs: hinge sie frei am Listenende,
     schöbe jede neue Position sie nach unten und die Hand müsste ihr folgen. */
  .lg-add { padding: 12px 16px 20px; position: sticky; bottom: 0; z-index: 2;
            background: #f0ede9; box-shadow: 0 -1px 0 var(--lg-line); }
  .lg-add--hero { position: static; box-shadow: none; padding-top: 16px; }
  /* Offen ist die Suche kein Anhängsel mehr, sondern das Hauptobjekt: Sie
     fordert die Höhe des Sichtfensters an (ZutatSuche) und rollt an dessen
     Oberkante. Klebte sie dabei weiter unten, wäre sie höher als der
     Scrollbereich — und ein klebendes Element, das nicht hineinpasst, rutscht
     beim Rollen sichtbar umher. */
  .lg-add--offen { position: static; }
  .lg-add__btn {
    display: flex; align-items: center; gap: 10px; width: 100%; box-sizing: border-box;
    min-height: 48px; padding: 0 12px; border: 1px dashed #d6d3d1; border-radius: 10px;
    background: none; font: inherit; font-size: 14px; font-weight: 600;
    color: var(--lg-ink-soft); cursor: pointer;
  }
  .lg-add__btn:hover { background: var(--lg-card); border-color: var(--lg-ink-mute); color: var(--lg-ink); }
  /* Tastaturfokus war bisher unsichtbar — die Zeile reagierte nur auf den
     Zeiger. Bei einer Maske, die man am Lieferschein entlang mit einer Hand
     bedient, ist das die falsche Hälfte der Bedienung. */
  .lg-add__btn:focus-visible {
    outline: none; border-style: solid; border-color: var(--lg-ink);
    color: var(--lg-ink); background: var(--lg-card); box-shadow: var(--lg-focus);
  }

  .lg-eb__foot {
    flex-shrink: 0; padding: 12px 16px 16px; background: rgba(245, 243, 240, 0.94);
    border-top: 1px solid var(--lg-line);
  }
  .lg-eb__journal { font-size: 11px; color: var(--lg-ink-mute); line-height: 1.45; margin: 0 0 10px; }
  .lg-kurz { display: block; margin-top: 4px; color: var(--lg-ink-soft); font-weight: 600; }
  .lg-cta {
    width: 100%; box-sizing: border-box; min-height: 54px; display: flex; align-items: center;
    justify-content: center; gap: 8px; border: 0; border-radius: 10px;
    background: var(--lg-accent); color: #fff; box-shadow: 0 6px 16px -8px rgba(192, 90, 62, 0.5);
    font: inherit; font-size: 15.5px; font-weight: 700; letter-spacing: -0.01em; cursor: pointer;
  }
  .lg-cta:hover:not(:disabled) { background: var(--lg-accent-hover); }
  .lg-cta:focus-visible { outline: 2px solid var(--lg-ink); outline-offset: 2px; }
  /* Der stumme Knopf trägt den einzigen Satz, der ihn erklärt — er muss lesbar
     bleiben, nicht nur ausgegraut sein. */
  .lg-cta:disabled { background: #e7e5e4; color: var(--lg-ink-soft); box-shadow: none; cursor: default; }

  /* ── Ab 600 px: derselbe Aufbau als Dialog über der Bestandsliste ──────── */
  /* Solange gesucht wird, tritt auf dem Handy alles zurück, was einmal je
     Lieferung gilt: Datum, Beleg, ihr Hinweis, der Hinweis zum Journal. Übrig
     bleiben die beiden Dinge, um die es in diesem Moment geht — die schon
     erfassten Positionen und die Vorschläge. Zusammen sind das rund 130 px,
     auf einem Bildschirm mit aufgestellter Tastatur ein Drittel des Bildes.
     Auf dem Desktop ist der Platz da; dort bleibt alles stehen. */
  @container (max-width: 599.98px) {
    .lg-eb--sucht .lg-eb__head { padding-top: 4px; padding-bottom: 4px; }
    .lg-eb--sucht .lg-eb__kopf { padding: 0; }
    .lg-eb--sucht .lg-eb__felder,
    .lg-eb--sucht .lg-eb__note,
    .lg-eb--sucht .lg-eb__journal { display: none; }
    .lg-eb--sucht .lg-eb__kurzkopf { display: flex; }
    .lg-eb--sucht .lg-eb__foot { padding: 10px 16px 14px; }
  }

  @container (min-width: 600px) {
    .lg-scrim { display: block; position: absolute; inset: 0; background: rgba(41, 37, 36, 0.30); }
    .lg-eb {
      position: relative; flex: 0 1 auto; margin: auto; width: 560px;
      max-height: min(86%, 760px); background: var(--lg-card);
      border: 1px solid var(--lg-line); border-radius: 16px;
      box-shadow: 0 24px 48px -24px rgba(28, 25, 23, 0.45); overflow: hidden;
    }
    .lg-eb__head { padding: 12px 12px 12px 20px; }
    .lg-eb__title { font-size: 17px; }
    .lg-eb__kopf { padding: 16px 20px; }
    .lg-eb__liste { background: var(--lg-card); }
    .lg-liste__kopf { padding: 14px 20px 8px; background: var(--lg-card); }
    .lg-pos { grid-template-columns: minmax(0, 1fr) 96px 48px 40px; padding-left: 20px; padding-right: 14px; }
    .lg-pos__meta { flex-basis: auto; }
    .lg-add { padding: 12px 20px 18px; background: var(--lg-card); }
    .lg-eb__foot { display: grid; grid-template-columns: 1fr auto; align-items: center; gap: 16px; padding: 14px 20px; }
    .lg-eb__journal { margin: 0; max-width: 260px; }
    .lg-cta { width: auto; padding: 0 18px; }
  }
</style>
